/**
 * suggestions.ts
 *
 * Static "things you can ask" prompts for the AI sidebar welcome screen,
 * chosen by route prefix. Deliberately hardcoded — generating these would
 * mean an LLM call just to open the panel, which is the cost problem this
 * whole screen exists to avoid.
 *
 * Clicking one sends it as an ordinary chat question.
 */
import type { PageContext } from '@/store/aiSidebarStore';
import type { ContextType } from '@/services/ai-context.service';

/** Suggestions shown when an entity is focused, keyed by entity type. */
const ENTITY_SUGGESTIONS: Record<ContextType, string[]> = {
  patient: [
    'Summarize this patient’s visit history',
    'What has this patient spent to date?',
    'Are there any open tasks for this patient?',
    'When were they last seen, and what’s next?',
  ],
  appointment: [
    'What should I know before this appointment?',
    'Has this patient missed appointments before?',
    'What was covered at their last visit?',
  ],
  lead: [
    'Summarize this lead',
    'What’s the next best action for this lead?',
    'How did this lead find us?',
  ],
  plan: [
    'Summarize this treatment plan',
    'What’s been paid and what’s outstanding?',
    'Which items on this plan are still incomplete?',
  ],
};

/**
 * Route prefix → suggestions. Longest matching prefix wins, so
 * '/marketing/automations' beats '/marketing'.
 */
const ROUTE_SUGGESTIONS: Array<[string, string[]]> = [
  [
    '/patients',
    [
      'How many patients match the current filters?',
      'Who was added most recently?',
      'Which of these patients haven’t visited in 6 months?',
    ],
  ],
  [
    '/appointments',
    [
      'What’s on the schedule today?',
      'How many no-shows have we had this week?',
      'Which appointments are still unconfirmed?',
    ],
  ],
  [
    '/schedule',
    [
      'What does today’s schedule look like?',
      'Where are the gaps in the schedule this week?',
      'Who has the busiest day?',
    ],
  ],
  [
    '/payments',
    [
      'What’s total revenue for this period?',
      'Are there any failed or declined payments?',
      'Which payment methods are used most?',
    ],
  ],
  [
    '/leads',
    [
      'Which leads haven’t been contacted yet?',
      'What’s our lead-to-patient conversion rate?',
      'Which lead sources are performing best?',
    ],
  ],
  [
    '/crm',
    [
      'Where are deals stalling in the pipeline?',
      'What changed in the pipeline this week?',
      'Which stage has the most leads?',
    ],
  ],
  [
    '/tasks',
    [
      'What’s overdue right now?',
      'Who has the most open tasks?',
      'What’s due today?',
    ],
  ],
  [
    '/marketing/automations',
    [
      'How is this automation performing?',
      'How many contacts have entered this automation?',
      'What’s the open and click rate?',
    ],
  ],
  [
    '/marketing/contact-lists',
    [
      'How many contacts are on this list?',
      'How was this list built?',
      'Which contacts are opted out?',
    ],
  ],
  [
    '/marketing',
    [
      'How did our last campaign perform?',
      'Which template gets the best open rate?',
      'What’s our overall opt-out rate?',
    ],
  ],
  [
    '/campaigns',
    [
      'How did our last campaign perform?',
      'Which campaign drove the most bookings?',
      'What’s the average open rate?',
    ],
  ],
  [
    '/memberships',
    [
      'How many active memberships do we have?',
      'Which membership tier is most popular?',
      'How many members cancelled this month?',
    ],
  ],
  [
    '/packages',
    [
      'Which packages sell the most?',
      'How many packages have unused sessions?',
      'What’s outstanding package revenue?',
    ],
  ],
  [
    '/gift-cards',
    [
      'How much gift-card value is outstanding?',
      'How many gift cards were sold this month?',
    ],
  ],
  [
    '/loans',
    ['How many loans are active?', 'What’s the total outstanding loan balance?'],
  ],
  [
    '/reports',
    [
      'Explain what this report shows',
      'What stands out in these results?',
      'How do I schedule this report?',
    ],
  ],
  [
    '/inventory',
    [
      'What’s running low on stock?',
      'Which vendor do we order from most?',
      'What’s the current inventory value?',
    ],
  ],
  [
    '/surveys',
    [
      'How many forms are awaiting review?',
      'What’s the completion rate on our forms?',
      'Which form is submitted most often?',
    ],
  ],
  [
    '/lab-orders',
    ['Which lab results came back abnormal?', 'What lab orders are still pending?'],
  ],
  [
    '/documents',
    ['What document types do we have on file?', 'How do I share a document with a patient?'],
  ],
  [
    '/video-calls',
    ['How many video calls happened this month?', 'What’s the average call duration?'],
  ],
  [
    '/plans',
    [
      'How many treatment plans are active?',
      'What’s the total value of open plans?',
      'Which plans are stalled?',
    ],
  ],
  [
    '/inbox',
    ['What needs a reply?', 'Summarize the unread messages'],
  ],
  [
    '/messages',
    ['What needs a reply?', 'Who have we not responded to?'],
  ],
  [
    '/alerts',
    ['What alerts need my attention?', 'Summarize today’s notifications'],
  ],
  [
    '/qa',
    ['What’s in the QA queue?', 'Which items have been flagged most often?'],
  ],
  [
    '/peptides',
    ['What peptides are we tracking?', 'How do I add a new peptide?'],
  ],
  // Reference/config surfaces — knowledge-base style questions suit these better
  // than data questions, and the backend has a KB search tool for exactly this.
  [
    '/admin',
    [
      'What do these settings control?',
      'How do I configure this correctly?',
      'What happens if I change this?',
    ],
  ],
  [
    '/help',
    ['How do I get support?', 'How do I submit a ticket?', 'Where do I find training?'],
  ],
  [
    '/knowledge',
    ['What training am I assigned?', 'Find articles about memberships', 'What’s new this month?'],
  ],
  [
    '/announcements',
    ['Summarize recent announcements', 'What did I miss this month?'],
  ],
  [
    '/settings',
    ['How do I change my notification preferences?', 'How do I update my profile?'],
  ],
];

const FALLBACK_SUGGESTIONS = [
  'What can I do on this page?',
  'Summarize what I’m looking at',
  'How do I use this feature?',
];

/**
 * Picks the suggestion set for the current context. Entity context wins over
 * route, since a focused record is the more specific thing to ask about.
 */
export function getSuggestions(
  pageContext: PageContext | null | undefined,
  contextType: ContextType | null | undefined,
): string[] {
  if (contextType && ENTITY_SUGGESTIONS[contextType]) {
    return ENTITY_SUGGESTIONS[contextType];
  }

  const route = pageContext?.route ?? '';
  let best: string[] | null = null;
  let bestLength = -1;

  for (const [prefix, list] of ROUTE_SUGGESTIONS) {
    if ((route === prefix || route.startsWith(`${prefix}/`)) && prefix.length > bestLength) {
      best = list;
      bestLength = prefix.length;
    }
  }

  return best ?? FALLBACK_SUGGESTIONS;
}
